import { prefsStore, useMoneyFormat, useSession } from '@adiox/ui';
import { lossRoom, lossTone } from '../challenge/model';
import { useChallenge } from '../challenge/useChallenge';
import { useStream } from '../stream/stream';
import { useActiveLogin } from '../trading/useTrading';
import { TONE_TEXT, marginTone } from './accountTone';
import { accountName, useAccountLabels } from './useAccountLabels';

const KIND_STYLE: Record<string, string> = {
  Challenge: 'border-accent/60 text-accent',
  Funded: 'border-up/60 text-up',
  Demo: 'border-line text-muted',
  Live: 'border-warn/60 text-warn',
};

function Figure({
  label,
  value,
  testId,
  tone,
  tip,
  note,
}: {
  label: string;
  value: string;
  testId: string;
  tone?: string;
  /** What the figure means: shown on hover, and read out with it. */
  tip: string;
  /** A word next to the value when it needs attention (never colour alone). */
  note?: string | null;
}) {
  return (
    <div className="flex flex-col" title={tip}>
      <span className="text-xs uppercase tracking-wide text-muted">{label}</span>
      <span className={`num text-sm font-semibold ${tone ?? ''}`}>
        <span data-testid={testId}>{value}</span>
        {note && (
          <span className="ml-1 text-[0.7rem] font-semibold uppercase" data-testid={`${testId}-note`}>
            {note}
          </span>
        )}
        <span className="sr-only">. {tip}</span>
      </span>
    </div>
  );
}

/**
 * The account figures. Values are shown exactly as the server computed them (the terminal never calculates money) and are
 * dimmed while the connection is not live, so stale numbers are never mistaken for current ones.
 */
export function AccountBar({ wrap = false }: { wrap?: boolean } = {}) {
  const session = useSession();
  const login = useActiveLogin();
  const accounts = session.user?.accounts ?? [];
  const labels = useAccountLabels();
  const account = useStream((s) => (login === undefined ? undefined : s.accounts[login]));
  const hubState = useStream((s) => s.hubState);
  const fmt = useMoneyFormat();
  const challenge = useChallenge().data;

  if (login === undefined) {
    return <span className="text-sm text-muted">No trading account</span>;
  }
  if (!account?.loaded) {
    return <span className="text-sm text-muted">Loading account…</span>;
  }

  const f = account.figures;
  const stale = hubState !== 'connected';
  const tone = marginTone(f, account);
  const levelTone = tone === 'danger' ? 'text-down' : tone === 'warn' ? 'text-warn' : '';
  const label = labels.get(login);
  const levels =
    account.stopOutLevel > 0
      ? ` Margin call at ${account.marginCallLevel}%, stop out at ${account.stopOutLevel}%: below that, positions are closed.`
      : '';
  return (
    <section
      aria-label="Account"
      className={`flex items-center ${wrap ? 'flex-wrap gap-x-6 gap-y-3' : 'gap-5'} ${stale ? 'opacity-50' : ''}`}
      data-testid="account-bar"
      data-stale={stale}
    >
      {accounts.length > 1 ? (
        <div className="flex items-center gap-2 text-sm text-muted">
          <label htmlFor="active-account" className="sr-only">
            Account
          </label>
          <select
            id="active-account"
            className="rounded-md border border-line bg-panel2 px-2 py-0.5 text-sm text-fg"
            value={login}
            onChange={(e) => prefsStore.set('activeLogin', Number(e.target.value))}
          >
            {[...accounts]
              .sort((a, b) => b - a)
              .map((a) => (
                <option key={a} value={a}>
                  {accountName(a, labels.get(a))}
                </option>
              ))}
          </select>
          <span>{account.currency}</span>
        </div>
      ) : (
        <span className="flex items-center gap-2 text-sm text-muted">
          #{login}
          {label && (
            <span
              className={`rounded border px-1.5 text-[0.7rem] font-semibold uppercase ${KIND_STYLE[label.kind] ?? ''}`}
              data-testid="account-kind"
            >
              {label.label}
            </span>
          )}
          {account.currency}
        </span>
      )}
      <Figure
        label="Balance"
        value={fmt(f.balance)}
        testId="balance"
        tip="Deposits, withdrawals and the results of closed trades."
      />
      {/* Equity includes credit, so once there is any it must be shown or the figures do not add up. */}
      {f.credit !== 0 && (
        <Figure
          label="Credit"
          value={fmt(f.credit)}
          testId="credit"
          tip="Money the broker has lent the account: it counts towards equity but cannot be withdrawn."
        />
      )}
      <Figure
        label="Equity"
        value={fmt(f.equity)}
        testId="equity"
        tip="Balance plus credit plus the floating profit or loss of open positions."
      />
      <Figure
        label="Floating"
        value={fmt(f.floating)}
        testId="floating"
        tone={f.floating > 0 ? 'text-up' : f.floating < 0 ? 'text-down' : ''}
        tip="Profit or loss of the open positions at current prices (swaps included)."
      />
      <Figure
        label="Margin"
        value={fmt(f.margin)}
        testId="margin"
        tip="Money set aside to keep the open positions open."
      />
      <Figure
        label="Free margin"
        value={fmt(f.freeMargin)}
        testId="free-margin"
        tone={f.freeMargin < 0 ? 'text-down' : ''}
        tip="Equity minus margin: what is left to open new positions."
      />
      <Figure
        label="Level"
        value={f.margin === 0 ? '—' : `${fmt(f.marginLevel)}%`}
        testId="margin-level"
        tone={levelTone}
        note={TONE_TEXT[tone]}
        tip={`Margin level: equity divided by margin.${levels}`}
      />
      {challenge && <LossLeft progress={challenge.current.progress} format={fmt} />}
      <Figure
        label="Positions"
        value={String(Object.keys(account.positions).length)}
        testId="position-count"
        tip="Open positions on this account."
      />
    </section>
  );
}

const TONE_CLASS = { ok: '', warn: 'text-warn', danger: 'text-down' } as const;

/**
 * A prop-firm account's room under its loss limits, always in view: what can still be lost today and in all before the
 * challenge fails. Amber from half of a limit used, red from 80 %, like the Challenge panel.
 */
function LossLeft({
  progress,
  format,
}: {
  progress: Parameters<typeof lossRoom>[0];
  format: (v: number) => string;
}) {
  const room = lossRoom(progress);
  const daily = lossTone(room.dailyUsedPercent);
  const max = lossTone(room.maxUsedPercent);
  return (
    <>
      <Figure
        label="Daily loss left"
        value={format(room.daily)}
        testId="daily-loss-left"
        tone={TONE_CLASS[daily]}
        note={daily === 'danger' ? 'Low' : null}
        tip={`What can still be lost today before the daily loss limit (${format(progress.dailyLossLimit)}) is broken: equity must stay above ${format(progress.dailyLossFloor)}. ${room.dailyUsedPercent.toFixed(0)}% used.`}
      />
      <Figure
        label="Max loss left"
        value={format(room.max)}
        testId="max-loss-left"
        tone={TONE_CLASS[max]}
        note={max === 'danger' ? 'Low' : null}
        tip={`What can still be lost in all before the maximum loss limit (${format(progress.maxLossLimit)}) is broken: equity must stay above ${format(progress.maxLossFloor)}. ${room.maxUsedPercent.toFixed(0)}% used.`}
      />
    </>
  );
}
